import { useRouter } from 'next/router';
import Script from 'next/script';
import React from 'react';

import type { GetServerSideProps, NextPage } from 'next';

import classNames from 'classnames';
import { connect } from 'react-redux';

import RegisterForm from '@/components/auth/RegisterForm';
import { AmbaAlert } from '@/components/global/AmbaAlert';
import AmbaImage from '@/components/global/AmbaImage';
import Button from '@/components/global/Button';
import { Shown } from '@/components/global/Shown';
import { RECAPTCHA_V3_SITEKEY } from '@/constants/env';
import { getUrlWithHost } from '@/helpers/url';
import useRegistrationColors from '@/hooks/useRegistrationColors';
import { useTranslation } from '@/i18n';
import { doRegister } from '@/redux/auth/actions';
import { doCustomizeRegister } from '@/redux/customizations/actions';
import { selectCompanyFormFields, selectRegisterCustomization } from '@/redux/customizations/reducers';
import { useAppDispatch } from '@/redux/hooks';
import { RootState } from '@/redux/rootReducer';
import { envVars } from '@/services/ApiService';
import { getCommonServerSideProps } from '@/util/getServerSideProps';
import { resolveUrlWithHostSsr } from '@/util/resolvePortalHost';
import LayoutConsume from '@/wrappers/layout/LayoutConsume';

import { RegisterFormData, RegisterPropsType } from '../models/authModels';
import { AmbaAlertTypes, ServerStatus } from '../models/globalModels';

const mapStateToProps = (state: RootState) => {
  const auth = state.auth;
  const customizations = state.customizations;
  return {
    customization: selectRegisterCustomization(state),
    formFields: selectCompanyFormFields(state),
    ambassadorStatus: auth.ambassadorStatus,
    userStatus: auth.userStatus,
    customizationStatus: customizations.customizationStatus,
    companyId: auth.companyAccount?.uid as number,
    companyName: auth.companyAccount?.company_name as string,
    registerStatusMessage: auth.registerStatusMessage,
    registerResponse: auth.registerResponse,
    registerStatus: auth.registerStatus,
  };
};

const Register: NextPage<RegisterPropsType> = ({
  ambassadorStatus = ServerStatus.IDLE,
  companyId,
  companyName,
  customization,
  customizationStatus = ServerStatus.IDLE,
  formFields,
  registerResponse,
  registerStatus = ServerStatus.IDLE,
  registerStatusMessage,
  userStatus = ServerStatus.IDLE,
  nonce,
}) => {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const router = useRouter();

  useRegistrationColors();

  const [validationMessage, setValidationMessage] = React.useState<string>('');
  const [showRegistrationMsg, setShowRegistrationMsg] = React.useState<boolean>(false);
  const [showRegisterForm, setShowRegisterForm] = React.useState<boolean>(true);
  const containerRef = React.useRef<null | HTMLDivElement>(null);
  const shortCode = (router.query.mbsy || '') as string;

  const handleRegistration = (formData: RegisterFormData) => {
    dispatch(doRegister({ ...formData, short_code: shortCode }, companyId, customization.portal_auto_login));
    containerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const redirectToLogin = () => {
    router.push(getUrlWithHost('/login', envVars.checkHost));
  };

  // sets the customization ready for register page
  React.useEffect(() => {
    if (customizationStatus == ServerStatus.FETCH && doCustomizeRegister) {
      dispatch(doCustomizeRegister(customization));
    }
  }, [customizationStatus]);

  // triggers an UI AmbaAlert with the response from the API
  React.useEffect(() => {
    if (registerStatusMessage && registerStatus === ServerStatus.FETCH_ERROR) {
      setValidationMessage(registerStatusMessage);
    } else if (registerStatus === ServerStatus.FETCH && registerResponse?.account) {
      // shows custom confirmation message and clears the form
      setShowRegistrationMsg(true);
      setShowRegisterForm(false);
      setValidationMessage('');
    }
  }, [registerStatusMessage, registerStatus, registerResponse]);

  // listens for auto login status and redirects to login process if enabled by company
  React.useEffect(() => {
    if (ambassadorStatus === ServerStatus.FETCH && userStatus === ServerStatus.FETCH && registerStatus === ServerStatus.FETCH) {
      //login page takes care of redirecting to dashboard if status is successful
      redirectToLogin();
    }
  }, [ambassadorStatus, userStatus, registerStatus]);

  React.useEffect(() => {
    if (customization.registration_page_redirect) {
      redirectToLogin();
    }
  }, [customization]);

  return (
    <>
      {customization?.recaptcha_enabled && (
        <Script src={`https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_V3_SITEKEY}`} strategy="afterInteractive" />
      )}

      <LayoutConsume menuActive={false}>
        <div className="RegisterPage overflow-hidden">
          <Shown>
            <div className="d-flex flex-row-reverse container px-5">
              <div className="loginBtnWrapper">
                <Button
                  className="loginBtn"
                  text={t('register.login')}
                  onClick={() => {
                    // reload page on login button click so I can get nonce to run script
                    const newUrl = getUrlWithHost('/login', envVars.checkHost);
                    window.location.href = newUrl;
                  }}
                />
              </div>
            </div>
            <div className="container px-5 py-3" ref={containerRef}>
              <div className="row py-5">
                <div
                  className={classNames(
                    'registerHeader col-md-12 justify-content-center',
                    customization.mobile_banner_image ? 'd-md-flex d-none' : 'd-flex',
                  )}
                >
                  <AmbaImage alt={companyName} imageSrc={customization.registration_page_logo} isLink={false} />
                </div>
                {customization.mobile_banner_image && (
                  <div className="registerHeader col-md-12 justify-content-center d-flex d-md-none">
                    <AmbaImage alt={companyName} imageSrc={customization.mobile_banner_image} isLink={false} />
                  </div>
                )}
              </div>
              <div className="row pb-5">
                <AmbaAlert
                  alertMessage={validationMessage}
                  data={registerResponse}
                  showAlert={validationMessage ? true : false}
                  type={AmbaAlertTypes.ERROR}
                />
                <AmbaAlert
                  alertMessage={registerStatusMessage || t('messages.registerAccount')}
                  showAlert={!validationMessage && registerStatus === ServerStatus.FETCH}
                  type={AmbaAlertTypes.SUCCESS}
                />
                {showRegisterForm ? (
                  <div className="leftCol col-md-6 px-4 border border-2 rounded">
                    <div className="formHeader mt-3" dangerouslySetInnerHTML={{ __html: customization.registration_page_form_header }} />
                    <RegisterForm
                      customization={customization}
                      formFields={formFields}
                      isLoading={registerStatus == ServerStatus.FETCHING}
                      onSubmit={handleRegistration}
                      validationResponse={registerResponse}
                    />
                  </div>
                ) : null}
                {showRegistrationMsg ? (
                  <div className="leftCol col-md-6 border border-2 rounded">
                    <div className="h-100" dangerouslySetInnerHTML={{ __html: customization.registration_page_message }} />
                  </div>
                ) : null}
                <div className="rightCol col-md-6 px-4 justify-content-center d-flex">
                  <div className="formInfo" dangerouslySetInnerHTML={{ __html: customization.registration_page_right_column }} />
                </div>
              </div>
            </div>
          </Shown>
        </div>
      </LayoutConsume>
    </>
  );
};

export default connect(mapStateToProps)(Register);

export const getServerSideProps: GetServerSideProps = async (context) => {
  const commonProps = await getCommonServerSideProps(context);
  const company = commonProps.company;

  if (company?.portal_v3_access && !company.portal_registration_access && !commonProps.user) {
    return {
      redirect: {
        destination: resolveUrlWithHostSsr('/login', envVars.checkHost, context),
        permanent: false,
      },
    };
  }
  return {
    props: {
      ...commonProps,
    },
  };
};
